import { webSocketWatcherEventTarget } from '@/components/UI/WebSocketWatcher';
import { type MutableRefObject, startTransition, useCallback, useEffect, useRef } from 'react';
import { useIsMounted } from '../hooks/useIsMounted';
import { useRefUpdating } from '../hooks/useRefUpdating';
import { useUpdateEffect } from '../hooks/useUpdateEffect';
import { IS_SERVER } from '../ssr';
import { useIdleContext } from '../useIdle';
import { injectIntoInfiniteData2 } from './infinite';
import {
  type InfiniteStore,
  PollingDelaysDefault,
  type UseInfiniteRequestItem,
  type UseInfiniteRequestOpts,
  type UseRequestOpts,
  type WithNew,
} from './types';

// Between these hours, use the extra long MAX pause
const BUSINESS_HOURS_START = 5;
const BUSINESS_HOURS_END = 23;

const ptTime = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/Los_Angeles',
  hour: 'numeric',
  hour12: false,
  hourCycle: 'h23',
});

// Randomization between polling for every client
const JITTER = 500;

// Items request limit while polling
const POLL_LIMIT = 10;

// Default key to use while polling
const ID = 'id' as const;

export function useNextInfinitePolling<
  const ItemType extends API.DbItem,
  const BodyType = void,
  const QueryType extends API.BasePageInput = API.WithPageInput<ItemType>,
  const MetaType = void,
  const FromSocketType = ItemType,
>(
  isClientReady: boolean,
  key: string,
  opts: UseInfiniteRequestOpts<ItemType, BodyType, QueryType, MetaType, FromSocketType>,
  initialOpts: UseRequestOpts<API.WithPage<ItemType[], MetaType>, QueryType>,
  knownIdsRef: MutableRefObject<Set<API.Id>>,
  requestFetcher,
  basePayload,
  storeBank: UseInfiniteRequestItem<ItemType, MetaType>,
  _storeGet: InfiniteStore<ItemType, MetaType>['get'],
  storeSet: InfiniteStore<ItemType, MetaType>['set'],
  _storeRemove: InfiniteStore<ItemType, MetaType>['remove'],
  wsReadyRef: MutableRefObject<boolean>,
) {
  const isClientReadyRef = useRefUpdating(isClientReady);

  const isMounted = useIsMounted();

  const pollingDelay = useRef((opts?.PollingDelays || PollingDelaysDefault).WS_CONNECTED_START);
  const PollingDelaysRef = useRefUpdating(opts?.PollingDelays || PollingDelaysDefault);
  const isPollingRunning = useRef<number | null>(null);

  const bank = storeBank?.data || basePayload?.data;
  const bankRef = useRefUpdating(bank);

  const initDoPollInAsc = initialOpts?.query?.order?.dir === 'DESC';
  const initPageCursor = bank?.pageInfo?.[initDoPollInAsc ? 'startCursor' : 'endCursor'];
  const initPageCursorId = bank?.pageInfo?.[initDoPollInAsc ? 'startCursorId' : 'endCursorId'];
  const pollingCursor =
    useRef<API.WithPageInfo<ItemType>['pageInfo']['startCursor']>(initPageCursor);
  const pollingCursorId = useRef<API.Id>(initPageCursorId || 0);

  const pollingTimer = useRef<NodeJS.Timeout>();

  const loop = useCallback(async () => {
    if (!isMounted() || opts?.skipPoll || !isClientReadyRef.current) {
      // don't even bother retrying the timer in this case
      return;
    }

    if (isPollingRunning.current) {
      // Prevent having this run multiple times
      clearTimeout(pollingTimer.current);
      pollingTimer.current = setTimeout(
        loop,
        pollingDelay.current + Math.round(Math.random() * JITTER),
      );
      return;
    }

    const pollingId = Math.random();

    isPollingRunning.current = pollingId;

    const currentHour = +ptTime.format(new Date());
    const isOob = currentHour < BUSINESS_HOURS_START || currentHour > BUSINESS_HOURS_END;

    try {
      const cursorCurrent = pollingCursor.current;
      const cursorCurrentId = pollingCursorId.current;
      const doPollInAsc = initialOpts?.query?.order?.dir === 'DESC';

      const res = await requestFetcher?.run?.(undefined, {
        dontStoreError: true,
        dontAlterState: true,
        rawResult: true,
        throwOnRun: true,
        query: {
          // Reuse other parts of query (ie. where clauses)
          ...initialOpts?.query,
          order: {
            // Reuse other parts of order, but reverse it
            ...initialOpts?.query?.order,
            by: initialOpts?.query?.order?.by || ID,
            dir:
              cursorCurrent !== undefined && cursorCurrent !== null
                ? doPollInAsc
                  ? 'ASC'
                  : 'DESC'
                : initialOpts?.query?.order?.dir || 'DESC',
          },
          page: {
            // Don't reuse anything from page
            [cursorCurrent ? 'after' : 'before']:
              cursorCurrent === null || cursorCurrent === undefined
                ? undefined
                : [cursorCurrent, cursorCurrentId],
            limit: POLL_LIMIT,
          },
        },
      });

      if (!res) {
        // this shouldn't happen...
        console.error(`useNextInfinitePolling: requestFetcher.run returned ${res}`);
        return;
      }

      if (isPollingRunning.current !== pollingId || !isMounted()) {
        clearTimeout(pollingTimer.current);
        pollingTimer.current = setTimeout(
          loop,
          pollingDelay.current + Math.round(Math.random() * JITTER),
        );
        return; // short circuit in case something went weird
      }

      if (res.data?.length) {
        // We found new data! Inject it in
        const newData: WithNew<ItemType>[] = [];

        // Update cursorCurrent
        const pageCursor = res.pageInfo[doPollInAsc ? 'startCursor' : 'endCursor'];
        const pageCursorId = res.pageInfo[doPollInAsc ? 'startCursorId' : 'endCursorId'];
        const nextCursor = pageCursor ? pageCursor : res.data[0].id || cursorCurrent;
        const nextCursorId = pageCursorId ? pageCursorId : res.data[0].id || cursorCurrentId;

        if (nextCursor !== cursorCurrent) {
          pollingCursor.current = nextCursor as any;
        }

        if (nextCursorId > cursorCurrentId) {
          pollingCursorId.current = nextCursorId;
        }

        for (const item of res.data) {
          // Avoid duplicates
          if (!knownIdsRef.current.has(item.id)) {
            // knownIdsRef.current.add(item.id);
            newData[cursorCurrent ? 'unshift' : 'push'](item);
          }
        }

        if (newData.length) {
          res.data = newData;

          startTransition(() => {
            storeSet(key, (draft, original) => {
              const origDataSet = original || bankRef.current;
              const newItems = injectIntoInfiniteData2(
                newData,
                origDataSet,
                draft.data,
                knownIdsRef,
                opts?.cursorKey,
                POLL_LIMIT,
                doPollInAsc ? 'start' : 'end',
              );

              if (newItems?.length) {
                const length = draft.data?.data?.length || 0;
                draft.isEmpty = length === 0;
              }

              if (opts?.event && newItems?.length) {
                for (const newItem of newItems) {
                  webSocketWatcherEventTarget.dispatchEvent(
                    new CustomEvent('ras:webSocketWatcher/created', {
                      detail: {
                        event: `${opts.event}/created`,
                        data: newItem,
                      },
                    }),
                  );
                  newItem;
                }
              }
            });
          });

          if (wsReadyRef.current) {
            pollingDelay.current = PollingDelaysRef.current.WS_CONNECTED_JUST_RECEIVED;
          } else {
            pollingDelay.current = PollingDelaysRef.current.JUST_RECEIVED;
          }
        }
      } else {
        // Data is empty; that means we're up to date, so use the latest cursorCurrent from websocket
        const nextCursor =
          bank?.data?.length && bank?.pageInfo[doPollInAsc ? 'startCursor' : 'endCursor'];
        const nextCursorId =
          bank?.data?.length && bank?.pageInfo[doPollInAsc ? 'startCursorId' : 'endCursorId'];

        if (nextCursor && nextCursor !== cursorCurrent) {
          pollingCursor.current = nextCursor;
        }

        if (nextCursorId && nextCursorId > cursorCurrentId) {
          pollingCursorId.current = nextCursorId;
        }

        if (wsReadyRef.current) {
          pollingDelay.current = Math.min(
            pollingDelay.current + PollingDelaysRef.current.WS_CONNECTED_INCREMENT,
            isOob
              ? PollingDelaysRef.current.WS_CONNECTED_MAX_OOB
              : PollingDelaysRef.current.WS_CONNECTED_MAX,
          );
        } else {
          pollingDelay.current = Math.min(
            pollingDelay.current + PollingDelaysRef.current.INCREMENT,
            PollingDelaysRef.current.MAX,
            isOob ? PollingDelaysRef.current.MAX_OOB : PollingDelaysRef.current.MAX,
          );
        }
      }
    } catch (error) {
      console.warn(error);

      if (wsReadyRef.current) {
        pollingDelay.current = Math.min(
          pollingDelay.current + PollingDelaysRef.current.WS_CONNECTED_INCREMENT,
          isOob
            ? PollingDelaysRef.current.WS_CONNECTED_MAX_OOB
            : PollingDelaysRef.current.WS_CONNECTED_MAX,
        );
      } else {
        pollingDelay.current = Math.min(
          pollingDelay.current + PollingDelaysRef.current.ERROR_INCREMENT,
          isOob ? PollingDelaysRef.current.MAX_OOB : PollingDelaysRef.current.MAX,
        );
      }
    }

    clearTimeout(pollingTimer.current); // just in case another one started up
    isPollingRunning.current = null;

    if (isMounted()) {
      // Only keep running if still mounted
      pollingTimer.current = setTimeout(
        loop,
        pollingDelay.current + Math.round(Math.random() * JITTER),
      );
    }
  }, [
    // Watch for specific options changes
    opts?.skipPoll,
    opts?.event,
    initialOpts?.query,
    // Watch if the useRequest changes
    requestFetcher?.run,
    // Watch if our key name changes
    key,
    // Watch if cursor target changes
    bank?.data?.length,
    bank?.pageInfo?.startCursor,
    bank?.pageInfo?.endCursorId,
  ]);

  // Force an immediate, fast catch-up poll. Used when the user reactivates the
  // app and — critically — when the WebSocket reconnects (see
  // useReconcileOnReconnect), so any `<event>/created` the socket dropped while
  // it flapped is fetched from the newest cursor within seconds instead of
  // staying invisible until a remount (#1210). The poll loop dedups against
  // knownIdsRef, so an extra catch-up is always safe.
  const pollNow = useCallback(() => {
    if (IS_SERVER || !isClientReadyRef.current) {
      return;
    }

    clearTimeout(pollingTimer.current); // just in case another one started up
    isPollingRunning.current = null;
    // Reset the backoff so the catch-up and the polls right after a reconnect
    // run at the fast cadence rather than whatever slow delay we'd backed off to.
    pollingDelay.current = PollingDelaysRef.current.START;

    pollingTimer.current = setTimeout(loop, Math.round(Math.random() * JITTER));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loop]);

  // Watch for idle to change polling rate
  const idleContext = useIdleContext();

  // Forcibly restart polling instantly when user reactivates
  useUpdateEffect(() => {
    if (!idleContext.inactive && isClientReady) {
      pollNow();
    }
  }, [pollNow, idleContext.inactive, isClientReady]);

  useEffect(() => {
    if (!IS_SERVER) {
      clearTimeout(pollingTimer.current);
      pollingTimer.current = setTimeout(
        loop,
        pollingDelay.current + Math.round(Math.random() * JITTER),
      );

      return () => {
        // clearTimeout(pollingTimer.current);
      };
    }
  }, [loop, opts?.PollingDelays]);

  return pollNow;
}
